import { toLocale } from '@zuruflex/i18n';
import {
  PROMOTION_FUNDERS,
  PROMOTION_SOURCES,
  PROMOTION_STATUSES,
  PROMOTION_TRIGGERS,
  type Promotion,
  type PromotionFunder,
  type PromotionSource,
  type PromotionStatus,
  type PromotionTrigger,
} from '@zuruflex/types';
import {
  Badge,
  Button,
  DataTable,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatTile,
  type ColumnDef,
} from '@zuruflex/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CirclePause, Plus, Sparkles, TicketPercent, Zap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PromotionDialog } from '../components/PromotionDialog.js';
import { api } from '../lib/api.js';
import {
  promotionState,
  promotionUses,
  promotionValue,
  promotionWindow,
  STATE_VARIANT,
} from '../lib/promotions.js';
import { useTableLabels } from '../lib/table-labels.js';

const ANY = 'any';

/**
 * Every promotion Zuruflex and its partners are running.
 *
 * Referral codes are left out unless asked for: every renter who shares a
 * link grows the list by one, and a page of FRIEND-7KQ2 rows would bury the
 * handful of promotions somebody actually decided to run.
 */
export function PromotionsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string>(ANY);
  const [funder, setFunder] = useState<string>(ANY);
  const [trigger, setTrigger] = useState<string>(ANY);
  const [source, setSource] = useState<PromotionSource>('MANUAL');
  const [creating, setCreating] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['promotions', { q, status, funder, trigger, source }],
    queryFn: () =>
      api.listPromotions({
        perPage: 100,
        source,
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(status !== ANY ? { status: status as PromotionStatus } : {}),
        ...(funder !== ANY ? { funder: funder as PromotionFunder } : {}),
        ...(trigger !== ANY ? { trigger: trigger as PromotionTrigger } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  // Partner names for the "paid by" column; a promo carries only the id.
  const partners = useQuery({
    queryKey: ['partners', 'all'],
    queryFn: () => api.listPartners({ perPage: 100 }),
  });
  const partnerName = useMemo(() => {
    const names = new Map((partners.data?.items ?? []).map((p) => [p.id, p.displayName]));
    return (id: string | null) => (id ? (names.get(id) ?? '—') : '—');
  }, [partners.data]);

  const rows = useMemo(() => data?.items ?? [], [data]);

  const totals = useMemo(() => {
    const now = new Date();
    return {
      live: rows.filter((promo) => promotionState(promo, now) === 'live').length,
      uses: rows.reduce((sum, promo) => sum + promo.redemptionCount, 0),
      automatic: rows.filter(
        (promo) => promo.trigger === 'AUTOMATIC' && promotionState(promo, now) === 'live',
      ).length,
      paused: rows.filter((promo) => promo.status === 'PAUSED').length,
    };
  }, [rows]);

  const columns = useMemo<ColumnDef<Promotion>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('common:field.name'),
        cell: ({ row }) => (
          <div className="min-w-0">
            <Link
              to={`/promotions/${row.original.id}`}
              onClick={(event) => event.stopPropagation()}
              className="font-medium underline-offset-2 hover:underline"
            >
              {row.original.name}
            </Link>
            <p className="text-xs text-text-muted">
              {row.original.code ? (
                <span className="font-mono">{row.original.code}</span>
              ) : (
                t('promotions.trigger.AUTOMATIC')
              )}
            </p>
          </div>
        ),
      },
      {
        id: 'value',
        header: t('promotions.column.value'),
        cell: ({ row }) => (
          <span className="font-semibold">{promotionValue(row.original, t, locale)}</span>
        ),
      },
      {
        accessorKey: 'funder',
        header: t('promotions.column.funder'),
        cell: ({ row }) =>
          row.original.funder === 'ZURUFLEX' ? (
            <Badge variant="soft">{t('promotions.funder.ZURUFLEX')}</Badge>
          ) : (
            <Badge variant="outline">{partnerName(row.original.partnerId)}</Badge>
          ),
      },
      {
        accessorKey: 'audience',
        header: t('promotions.column.audience'),
        cell: ({ row }) => (
          <span className="text-sm text-text-muted">
            {t(`promotions.audience.${row.original.audience}`)}
          </span>
        ),
      },
      {
        id: 'window',
        header: t('promotions.column.window'),
        cell: ({ row }) => (
          <span className="text-sm text-text-muted">
            {promotionWindow(row.original, t, locale)}
          </span>
        ),
      },
      {
        accessorKey: 'redemptionCount',
        header: t('promotions.column.uses'),
        cell: ({ row }) => <span className="tabular-nums">{promotionUses(row.original, t)}</span>,
      },
      {
        accessorKey: 'status',
        header: t('common:field.status'),
        cell: ({ row }) => {
          const state = promotionState(row.original);
          return <Badge variant={STATE_VARIANT[state]}>{t(`promotions.state.${state}`)}</Badge>;
        },
      },
    ],
    [t, locale, partnerName],
  );

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('promotions.title')}
        description={t('promotions.subtitle')}
        action={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            {t('promotions.new')}
          </Button>
        }
        className="mb-0"
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label={t('promotions.stat.live')}
          value={String(totals.live)}
          hint={t('promotions.stat.liveHint')}
          icon={<Sparkles className="size-4" />}
          isLoading={isPending}
          accent
        />
        <StatTile
          label={t('promotions.stat.uses')}
          value={totals.uses.toLocaleString(locale)}
          hint={t('promotions.stat.usesHint')}
          icon={<TicketPercent className="size-4" />}
          isLoading={isPending}
        />
        <StatTile
          label={t('promotions.stat.automatic')}
          value={String(totals.automatic)}
          hint={t('promotions.stat.automaticHint')}
          icon={<Zap className="size-4" />}
          isLoading={isPending}
        />
        <StatTile
          label={t('promotions.stat.paused')}
          value={String(totals.paused)}
          hint={t('promotions.stat.pausedHint')}
          icon={<CirclePause className="size-4" />}
          isLoading={isPending}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <FormField label={t('common:action.search')}>
          {(field) => (
            <Input
              {...field}
              placeholder={t('promotions.searchPlaceholder')}
              value={q}
              onChange={(event) => setQ(event.target.value)}
            />
          )}
        </FormField>

        <FilterSelect
          label={t('common:field.status')}
          value={status}
          onChange={setStatus}
          anyLabel={t('promotions.anyStatus')}
          options={PROMOTION_STATUSES.map((value) => ({
            value,
            label: t(`promotions.status.${value}`),
          }))}
        />
        <FilterSelect
          label={t('promotions.column.funder')}
          value={funder}
          onChange={setFunder}
          anyLabel={t('promotions.anyFunder')}
          options={PROMOTION_FUNDERS.map((value) => ({
            value,
            label: t(`promotions.funder.${value}`),
          }))}
        />
        <FilterSelect
          label={t('promotions.form.trigger')}
          value={trigger}
          onChange={setTrigger}
          anyLabel={t('promotions.anyTrigger')}
          options={PROMOTION_TRIGGERS.map((value) => ({
            value,
            label: t(`promotions.trigger.${value}`),
          }))}
        />
        <FilterSelect
          label={t('promotions.column.source')}
          value={source}
          onChange={(value) => setSource(value as PromotionSource)}
          options={PROMOTION_SOURCES.map((value) => ({
            value,
            label: t(`promotions.source.${value}`),
          }))}
        />
      </div>

      <DataTable
        labels={tableLabels}
        columns={columns}
        data={rows}
        isLoading={isPending}
        onRowClick={(row) => navigate(`/promotions/${row.id}`)}
        emptyMessage={t('promotions.none')}
      />

      {creating ? (
        <PromotionDialog
          onClose={() => setCreating(false)}
          onSaved={(saved) => navigate(`/promotions/${saved.id}`)}
        />
      ) : null}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  anyLabel,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  /** Offers an "any" choice when given. */
  anyLabel?: string;
}) {
  return (
    <FormField label={label}>
      {(field) => (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={field.id}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {anyLabel ? <SelectItem value={ANY}>{anyLabel}</SelectItem> : null}
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </FormField>
  );
}
